import React, { useState, useEffect, useRef } from 'react';
import '../../css/Cliente.css';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import useDefaultModal from '../hooks/useDefaultModal';
import DefaultModal from '../modals/DefaultModal';
import { formatString } from '../../utils/StringUtils';

const CHAR_LIMITS = {
  objeto: 500,
  justificativaPrioridade: 500,
  justificativaDemanda: 5000,
  regulamentacao: 50,
};

const DEFAULT_FORM_DATA = {
  cnpj: '',
  num: '',
  dataCriacao: '',
  dataContratacao: '',
  requisitante: '',
  objeto: '',
  prioridade: '',
  justificativaPrioridade: '',
  justificativaDemanda: '',
  tipo: '',
  tipoObjeto: '',
  natureza: '',
  usuario: '',
  tramitacao: '',
  anoBase: '',
  tipoDemanda: '',
  regulamentacao: '',
  agrupamento: '',
  numeroContrato: '',
  vigenciaContrato: '',
  tipoInstrumento: '',
  cpfCnpjFornecedor: '',
  nomeFornecedor: '',
  dataConclusaoDfd: '',
};

function DfdModal({ isOpen, onClose, initialDfd = null, onSaved = null }) {
  const today = new Date().toISOString().split('T')[0]; // yyyy-mm-dd
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const [requisitantes, setRequisitantes] = useState([]);
  const [naturezas, setNaturezas] = useState([]);
  const [tiposObjeto, setTiposObjeto] = useState([]);
  const [hasItensOrLotes, setHasItensOrLotes] = useState(false);
  const [charCounts, setCharCounts] = useState({
    objeto: 0,
    justificativaPrioridade: 0,
    justificativaDemanda: 0,
  });
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase = localStorage.getItem('anoBase');
  const login = localStorage.getItem('login');
  const sourceDfd = initialDfd || null;
  const descricaoRef = useRef(null);
  const justificativaPrioridadeRef = useRef(null);
  const justificativaDemandaRef = useRef(null);

  const canSave =
    !formData.tramitacao ||
    formData.tramitacao === 'EM ELABORAÇÃO' ||
    formData.tramitacao === 'DEVOLVIDO PARA AJUSTES';

  const handleClose = () => {
    if (onClose) onClose();
  };

  // Função para ajustar a altura do textarea
  const adjustTextareaHeight = textarea => {
    if (textarea) {
      textarea.style.height = 'auto'; // Reseta a altura
      textarea.style.height = `${textarea.scrollHeight}px`; // Ajusta a altura com base no conteúdo
    }
  };

  // Formata CPF ou CNPJ para exibição (simples)
  function formatCpfCnpj(value) {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 11) {
      // CPF: 000.000.000-00
      return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      // CNPJ: 00.000.000/0000-00
      return digits
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }
  }

  function validateCpfCnpj(value) {
    const digits = value.replace(/\D/g, '');
    return digits.length === 11 || digits.length === 14;
  }

  // Ajusta a altura dos textareas sempre que o conteúdo mudar
  useEffect(() => {
    adjustTextareaHeight(descricaoRef.current);
    adjustTextareaHeight(justificativaPrioridadeRef.current);
    adjustTextareaHeight(justificativaDemandaRef.current);
  }, [
    formData.objeto,
    formData.justificativaPrioridade,
    formData.justificativaDemanda,
  ]);

  useEffect(() => {
    if (!isOpen) return;

    if (sourceDfd) {
      const dfdData = sourceDfd;
      // Formata CPF/CNPJ para exibição no formulário (mantém máscara)
      setFormData({
        ...DEFAULT_FORM_DATA,
        ...dfdData,
        dataCriacao: dfdData.dataCriacao || '', // usa a data original do DFD
        cpfCnpjFornecedor: dfdData.cpfCnpjFornecedor
          ? formatCpfCnpj(String(dfdData.cpfCnpjFornecedor))
          : '',
        cnpj: cnpj || dfdData.cnpj,
        usuario: login || dfdData.usuario,
        anoBase: anoBase || dfdData.anoBase || '',
        tipoObjeto: dfdData.tipoObjeto || '',
      });
      
      // Verifica se já há itens ou lotes cadastrados
      const hasItens =
        (Array.isArray(dfdData.dfdItens) && dfdData.dfdItens.length > 0) ||
        (Array.isArray(dfdData.dfdItemSet) && dfdData.dfdItemSet.length > 0);
      const hasLotes = Array.isArray(dfdData.dfdLoteSet) && dfdData.dfdLoteSet.length > 0;
      setHasItensOrLotes(hasItens || hasLotes);
    } else {
      // Novo DFD: preenche data de criação com hoje
      setFormData({
        ...DEFAULT_FORM_DATA,
        cnpj: cnpj || '',
        usuario: login || '',
        dataCriacao: today,
        anoBase: anoBase || '',
      });
      setHasItensOrLotes(false);
    }

    axios
      .get(`${API_BASE_URL.REQUISITANTE}/all_cnpj`, {
        params: { cnpj: cnpj },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        setRequisitantes(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter listas:', error);
      });
    axios
      .get(`${API_BASE_URL.NATUREZA}/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        setNaturezas(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter listas:', error);
      });
  }, [isOpen, sourceDfd, cnpj, login, today, anoBase, token]);

  // useEffect para carregar tipos de objeto quando tipo = MATERIAL
  useEffect(() => {
    if (formData.tipo === 'MATERIAL') {
      axios
        .get(`${API_BASE_URL.TIPO_OBJETO}/all`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(result => {
          setTiposObjeto(result.data);
        })
        .catch(error => {
          console.error('Erro ao obter tipos de objeto:', error);
        });
    } else {
      setTiposObjeto([]);
      // Limpa o tipoObjeto sempre que tipo != MATERIAL
      if (formData.tipoObjeto) {
        setFormData(prevData => ({ ...prevData, tipoObjeto: '' }));
      }
    }
  }, [formData.tipo, token]);

  // useEffect específico para carregar tipos de objeto quando há dados iniciais
  useEffect(() => {
    if (sourceDfd?.tipo === 'MATERIAL' && tiposObjeto.length === 0) {
      axios
        .get(`${API_BASE_URL.TIPO_OBJETO}/all`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(result => {
          setTiposObjeto(result.data);
        })
        .catch(error => {
          console.error('Erro ao obter tipos de objeto:', error);
        });
    }
  }, [
    sourceDfd?.tipo,
    token,
    tiposObjeto.length,
    formData.tipoObjeto,
  ]);

  useEffect(() => {
    setCharCounts({
      objeto: formData.objeto.length,
      justificativaPrioridade: formData.justificativaPrioridade.length,
      justificativaDemanda: formData.justificativaDemanda.length,
    });
  }, [formData]);

  function handleChange(event) {
    const { name, value } = event.target;
    // Se usuário mudou o tipo de demanda, limpar campos de renovação quando sair deste tipo
    if (name === 'tipoDemanda') {
      if (value !== 'RENOVAÇÃO DE CONTRATO') {
        setFormData({
          ...formData,
          tipoDemanda: value,
          numeroContrato: '',
          cpfCnpjFornecedor: '',
          nomeFornecedor: '',
        });
        return;
      }
    }
    setFormData({ ...formData, [name]: value });
  }

  function handleCpfCnpjChange(event) {
    const raw = event.target.value;
    const formatted = formatCpfCnpj(raw);
    setFormData({ ...formData, cpfCnpjFornecedor: formatted });
  }

  function handleDescricaoInput(event) {
    const { name, value, selectionStart } = event.target;
    const newValue = value.slice(0, CHAR_LIMITS[name]);
    setFormData(prevState => ({
      ...prevState,
      [name]: newValue,
    }));

    setCharCounts(prevCounts => ({
      ...prevCounts,
      [name]: newValue.length,
    }));

    setTimeout(() => {
      if (name === 'objeto') {
        descricaoRef.current.selectionStart = selectionStart;
        descricaoRef.current.selectionEnd = selectionStart;
      } else if (name === 'justificativaPrioridade') {
        justificativaPrioridadeRef.current.selectionStart = selectionStart;
        justificativaPrioridadeRef.current.selectionEnd = selectionStart;
      } else if (name === 'justificativaDemanda') {
        justificativaDemandaRef.current.selectionStart = selectionStart;
        justificativaDemandaRef.current.selectionEnd = selectionStart;
      }
    }, 0);
  }

  function handleSubmit(event) {
    event.preventDefault();
    const updatedTramitacao =
      !formData.tramitacao ||
      formData.tramitacao === '' ||
      formData.tramitacao === null
        ? 'EM ELABORAÇÃO'
        : formData.tramitacao === 'DEVOLVIDO PARA CORREÇÃO'
        ? 'EM CORREÇÃO'
        : formData.tramitacao;

    setFormData(prevFormData => ({
      ...prevFormData,
      tramitacao: updatedTramitacao,
    }));
    const updatedFormData = {
      ...formData,
      tramitacao: updatedTramitacao,
      anoBase: localStorage.getItem('anoBase'),
      // Garante que tipoObjeto seja null se tipo != MATERIAL
      tipoObjeto: formData.tipo === 'MATERIAL' ? formData.tipoObjeto : null,
    };

    if (
      !formData.dataCriacao ||
      !formData.dataContratacao ||
      !formData.tipo ||
      !formData.requisitante ||
      !formData.objeto ||
      !formData.tipoDemanda ||
      !formData.regulamentacao ||
      !formData.agrupamento ||
      (formData.tipo === 'MATERIAL' && !formData.tipoObjeto)
    ) {
      const missingFields = [];
      if (!formData.dataCriacao) missingFields.push('Data de Criação');
      if (!formData.dataContratacao) missingFields.push('Data da Contratação');
      if (!formData.tipo) missingFields.push('Tipo');
      if (!formData.requisitante) missingFields.push('Requisitante');
      if (!formData.objeto) missingFields.push('Objeto');
      if (!formData.tipoDemanda) missingFields.push('Tipo de Demanda');
      if (!formData.agrupamento) missingFields.push('Item ou Lote');
      if (!formData.regulamentacao)
        missingFields.push('Regulamentação Municipal');
      if (formData.tipo === 'MATERIAL' && !formData.tipoObjeto)
        missingFields.push('Tipo de Objeto');

        // Campos obrigatórios para Renovação de Contrato
        if (formData.tipoDemanda === 'RENOVAÇÃO DE CONTRATO') {
          if (!formData.numeroContrato || formData.numeroContrato.trim() === '')
            missingFields.push('Número do Contrato');
          if (!formData.cpfCnpjFornecedor || formData.cpfCnpjFornecedor.trim() === '')
            missingFields.push('CPF/CNPJ do Fornecedor');
          if (!formData.nomeFornecedor || formData.nomeFornecedor.trim() === '')
            missingFields.push('Nome ou Razão Social do Fornecedor');
        }

      const plural = missingFields.length > 1 ? 'os campos' : 'o campo';
      const fields = missingFields.join(', ');

      showDefaultModal('error', `Por favor, preencha ${plural}: ${fields}.`);
      return;
    }

    // Validação específica de CPF/CNPJ quando for Renovação de Contrato
    if (
      formData.tipoDemanda === 'RENOVAÇÃO DE CONTRATO' &&
      !validateCpfCnpj(formData.cpfCnpjFornecedor || '')
    ) {
      showDefaultModal('error', 'CPF/CNPJ do Fornecedor inválido.');
      return;
    }

    // Validação do ano da Data da Contratação
    const anoBaseSelecionado = parseInt(localStorage.getItem('anoBase'));
    // Extrai o ano diretamente da string da data (formato YYYY-MM-DD)
    const anoDataContratacao = parseInt(formData.dataContratacao.split('-')[0]);

    if (anoDataContratacao !== anoBaseSelecionado) {
      showDefaultModal(
        'error',
        `O ano da Data da Contratação (${anoDataContratacao}) deve ser igual ao Ano Base selecionado (${anoBaseSelecionado}).
        Atualize a data ou troque o Ano Base.`
      );
      return;
    }
    // Remove máscara de CPF/CNPJ antes de enviar ao backend
    const sendData = {
      ...updatedFormData,
      cpfCnpjFornecedor: (updatedFormData.cpfCnpjFornecedor || '').replace(/\D/g, ''),
    };

    axios
      .post(`${API_BASE_URL.DFD}/save`, sendData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        showDefaultModal('success', 'O registro foi salvo com sucesso!', () => {
          if (onSaved) onSaved();
          handleClose();
        });
      })
      .catch(error => {
        console.error('Erro ao salvar demanda:', error);
        showDefaultModal(
          'error',
          'Erro ao salvar o registro. Tente novamente.'
        );
      });
  }

  const formContent = (
      <div
        className="d-flex justify-content-between w-100 small mb-0 p-2"
        style={{
          flex: '1 1 auto',
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingRight: '0.5rem',
        }}
      >
        <div>
          <form id="dfdForm" className="small" onSubmit={handleSubmit}>
            <div className="row g-2 small">
              <div className="col-md-2 col-sm-6">
                <label className="form-label fw-semibold mb-1">
                  Data de Criação
                </label>
                <input
                  onChange={handleChange}
                  value={formData.dataCriacao || ''}
                  name="dataCriacao"
                  type="date"
                  id="dataCriacao"
                  className="form-control form-control-sm"
                  readOnly
                  disabled
                />
              </div>
              <div className="col-md-2 col-sm-6">
                <label
                  className="form-label fw-semibold mb-1"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="Previsão de contratação"
                >
                  Data Prevista da Contratação
                </label>
                <input
                  onChange={handleChange}
                  value={formData.dataContratacao || ''}
                  name="dataContratacao"
                  type="date"
                  id="dataContratacao"
                  className="form-control form-control-sm"
                />
              </div>

              <div
                className={`${
                  formData.tipo === 'MATERIAL' ? 'col-md-3' : 'col-md-4'
                } col-sm-6`}
              >
                <label className="form-label fw-semibold mb-1">
                  Área Requisitante
                </label>
                <select
                  className="form-select form-select-sm"
                  value={formData.requisitante}
                  onChange={handleChange}
                  name="requisitante"
                >
                  <option value="">Selecione...</option>
                  {requisitantes.map((option, index) => (
                    <option key={index} value={option.nome}>
                      {option.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div
                className={`${
                  formData.tipo === 'MATERIAL' ? 'col-md-2' : 'col-md-4'
                } col-sm-6`}
              >
                <label className="form-label fw-semibold mb-1">Tipo</label>
                <select
                  className="form-select form-select-sm"
                  value={formData.tipo}
                  onChange={handleChange}
                  name="tipo"
                >
                  <option value="">Selecione...</option>
                  <option value="AQUISIÇÃO DE IMÓVEIS">AQUISIÇÃO DE IMÓVEIS</option>
                  <option value="MATERIAL">MATERIAL</option>
                  <option value="OBRA">OBRA</option>
                  <option value="SERVIÇO">SERVIÇO</option>
                  <option value="SERVIÇO DE ENGENHARIA">
                    SERVIÇO DE ENGENHARIA
                  </option>
                </select>
              </div>
              {formData.tipo === 'MATERIAL' && (
                <div className="col-md-3 col-sm-6">
                  <label className="form-label fw-semibold mb-1">
                    Tipo de Objeto <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={formData.tipoObjeto || ''}
                    onChange={handleChange}
                    name="tipoObjeto"
                    required
                  >
                    <option value="">Selecione...</option>
                    {tiposObjeto.map(option => (
                      <option key={option.id} value={option.descricao}>
                        {option.descricao}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="col-12">
                <label className="form-label fw-semibold mb-1">
                  Descrição do Objeto
                  <span className="text-muted ms-2">
                    ({charCounts.objeto}/{CHAR_LIMITS.objeto})
                  </span>
                </label>
                <textarea
                  className="form-control form-control-sm"
                  rows="2"
                  ref={descricaoRef}
                  onInput={handleDescricaoInput}
                  value={formData.objeto}
                  name="objeto"
                  style={{ overflow: 'hidden', resize: 'none' }}
                  placeholder="Descreva detalhadamente o objeto da contratação..."
                />
              </div>

              <div className="col-lg-3 col-md-6">
                <label className="form-label fw-semibold mb-1">
                  Tipo de Demanda
                </label>
                <select
                  className="form-select form-select-sm"
                  value={formData.tipoDemanda}
                  onChange={handleChange}
                  name="tipoDemanda"
                >
                  <option value="">Selecione...</option>
                  <option value="NOVA CONTRATAÇÃO">NOVA CONTRATAÇÃO</option>
                  <option value="RENOVAÇÃO DE CONTRATO">
                    RENOVAÇÃO DE CONTRATO
                  </option>
                </select>
              </div>
              {formData.tipoDemanda === 'RENOVAÇÃO DE CONTRATO' && (
                <>
                <div className="col-lg-2 col-sm-6">
                    <label className="form-label fw-semibold mb-1">Tipo de Instrumento</label>
                    <select
                      className="form-select form-select-sm"
                      name="tipoInstrumento"
                      value={formData.tipoInstrumento}
                      onChange={handleChange}
                    >
                      <option value="">Selecione...</option>
                      <option value="Contrato">Contrato</option>
                      <option value="ARP">ARP</option>
                    </select>
                  </div>
                  <div className="col-lg-3 col-sm-6">
                    <label className="form-label fw-semibold mb-1">Número do Contrato</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="numeroContrato"
                      value={formData.numeroContrato}
                      onChange={handleChange}
                      placeholder="Número do contrato"
                    />
                  </div>
                  <div className="col-lg-2 col-sm-6">
                    <label className="form-label fw-semibold mb-1">Vigência do Contrato</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      name="vigenciaContrato"
                      value={formData.vigenciaContrato}
                      onChange={handleChange}
                      placeholder="Vigência"
                    />
                  </div>
                  <div className="col-lg-2 col-sm-6">
                    <label className="form-label fw-semibold mb-1">CPF/CNPJ do Fornecedor</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="cpfCnpjFornecedor"
                      value={formData.cpfCnpjFornecedor}
                      onChange={handleCpfCnpjChange}
                      placeholder="CPF ou CNPJ"
                    />
                  </div>
                  <div className="col-lg-3 col-sm-6">
                    <label className="form-label fw-semibold mb-1">Nome ou Razão Social do Fornecedor</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="nomeFornecedor"
                      value={formData.nomeFornecedor}
                      onChange={handleChange}
                      placeholder="Nome ou razão social"
                    />
                  </div>
                </>
              )}
              <div className="col-lg-2 col-sm-6">
                <label className="form-label fw-semibold mb-1">Agrupamento</label>
                <select
                  className="form-select form-select-sm"
                  value={formData.agrupamento}
                  onChange={handleChange}
                  name="agrupamento"
                  required
                  disabled={hasItensOrLotes}
                  title={hasItensOrLotes ? 'Não é possível alterar o agrupamento pois já existem itens ou lotes cadastrados.' : ''}
                >
                  <option value="">Selecione...</option>
                  <option value="ITEM">ITEM</option>
                  <option value="LOTE">LOTE</option>
                </select>
              </div>
              <div className="col-lg-5 col-md-12">
                <label className="form-label fw-semibold mb-1">
                  Natureza da Despesa
                </label>
                <select
                  className="form-select form-select-sm"
                  value={formData.natureza}
                  onChange={handleChange}
                  name="natureza"
                >
                  <option value="">Selecione...</option>
                  {naturezas.map((option, index) => (
                    <option key={index} value={option.codigo}>
                      {option.codigo} - {option.descricao}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-2 col-md-6">
                <label className="form-label fw-semibold mb-1">Prioridade</label>
                <select
                  className="form-select form-select-sm"
                  value={formData.prioridade}
                  onChange={handleChange}
                  name="prioridade"
                >
                  <option value="">Selecione...</option>
                  <option value="BAIXA">BAIXA</option>
                  <option value="MÉDIA">MÉDIA</option>
                  <option value="ALTA">ALTA</option>
                </select>
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold mb-1">
                  Justificativa da Prioridade
                  <span className="text-muted ms-2">
                    ({charCounts.justificativaPrioridade}/
                    {CHAR_LIMITS.justificativaPrioridade})
                  </span>
                </label>
                <textarea
                  className="form-control form-control-sm"
                  rows="2"
                  ref={justificativaPrioridadeRef}
                  onInput={handleDescricaoInput}
                  value={formData.justificativaPrioridade}
                  name="justificativaPrioridade"
                  style={{ overflow: 'hidden', resize: 'none' }}
                  placeholder="Justifique a prioridade selecionada..."
                />
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold mb-1">
                  Justificativa do DFD
                  <span className="text-muted ms-2">
                    ({charCounts.justificativaDemanda}/
                    {CHAR_LIMITS.justificativaDemanda})
                  </span>
                </label>
                <textarea
                  className="form-control form-control-sm"
                  rows="2"
                  ref={justificativaDemandaRef}
                  onInput={handleDescricaoInput}
                  value={formData.justificativaDemanda}
                  name="justificativaDemanda"
                  style={{ overflow: 'hidden', resize: 'none' }}
                  placeholder="Apresente a justificativa detalhada da demanda..."
                />
              </div>

              <div className="col-lg-6 col-md-12">
                <label className="form-label fw-semibold mb-1">
                  Regulamentação Municipal
                  <span className="text-muted small d-block d-sm-inline">
                    (Lei, Decreto ou Portaria que regulamentou a Lei Federal nº
                    14.133/2021)
                  </span>
                  <span className="text-muted ms-2">
                    (
                    {formData.regulamentacao
                      ? formData.regulamentacao.length
                      : 0}
                    /{CHAR_LIMITS.regulamentacao})
                  </span>
                </label>
                <input
                  onChange={handleDescricaoInput}
                  value={formData.regulamentacao}
                  name="regulamentacao"
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Ex: Lei Municipal nº 1234/2022"
                />
              </div>
              <div className="col-lg-2 col-md-12">
                <label className="form-label fw-semibold mb-1">Data de conclusão do DFD</label>
                <input
                  type="date"
                  name="dataConclusaoDfd"
                  value={formData.dataConclusaoDfd || ''}
                  className="form-control form-control-sm"
                  readOnly
                />
              </div>
            </div>
          </form>
        </div>
      </div>
  );

  if (!isOpen) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
        <div className="modal-dialog modal-xl" style={{ maxWidth: '95vw', height: '90vh' }}>
          <div className="modal-content h-100">
            <div className="modal-header text-white py-1" style={{ background: 'linear-gradient(90deg, #0b3383, #0e48ad)' }}>
              <h6 className="modal-title m-0 small">
                <i className="bi bi-file-earmark-text me-2" />
                {`Cadastro de DFD${formData.num ? ` - Nº ${formatString(formData.num, 4)}` : ''}`}
              </h6>
              <button
                className="btn btn-link text-white p-0 mb-0"
                onClick={handleClose}
                title="Fechar"
                style={{ fontSize: '0.9rem', textDecoration: 'none' }}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="modal-body p-2" style={{ flex: 1, overflow: 'hidden' }}>
              {formContent}
            </div>
            <div className="modal-footer py-1">
              <button type="button" className="btn btn-secondary btn-sm" onClick={handleClose}>Fechar</button>
              {canSave && (
                <button
                  type="submit"
                  form="dfdForm"
                  className="btn btn-success btn-sm d-inline-flex align-items-center"
                >
                  <i className="bi bi-check-lg me-1" />
                  Salvar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <DefaultModal {...setDefaultModal} />
    </>
  );
}

export default DfdModal;
